import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Popover, TextField, Tooltip } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import React, { useState, useEffect, useMemo, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { ThemeContext } from '../../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { setFilteredHistory } from '../../../store/Slices/historySlice';
import { IoTimerOutline } from "react-icons/io5";

export default function HistoryFilter() {
    const [filterPeriod, setFilterPeriod] = useState("today");
    const [toDate, setToDate] = useState(new Date());
    const [fromDate, setFromDate] = useState(new Date());
    const [customDateRange, setCustomDateRange] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const selectedSymbol = useSelector((state) => state.history.setSelectedSymbol)
    const selectedTabType = useSelector((state) => state.history.selectedTabType)
    const { darkMode } = useContext(ThemeContext)
    const { t } = useTranslation()
    const [showFilter, setShowFilter] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const handleOpenMenu = (event) => {
        setAnchorEl(event.currentTarget);
        setShowFilter(prev => !prev)
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const accountHistory = useSelector((state) => state.orderPosition.accountHistory);
    const creation = localStorage.getItem('creation');

    const formatDateWithoutMilliseconds = (date) => {
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");
        const seconds = String(d.getSeconds()).padStart(2, "0");

        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    };

    const getStartOfDay = (date) => {
        return formatDateWithoutMilliseconds(new Date(date.setHours(0, 0, 0, 0)));
    };

    const getStartOfLastWeek = () => {
        let date = new Date();
        let day = date.getDay(); // Get current day (0 = Sunday, 1 = Monday, ..., 6 = Saturday)

        let diffToLastMonday = day === 0 ? -6 : 1 - day; // Adjust to the previous Monday
        date.setDate(date.getDate() + diffToLastMonday - 7); // Move to last week's Monday
        date.setHours(0, 0, 0, 0); // Start of the day

        return date;
    };

    const getEndOfLastWeek = () => {
        let startOfLastWeek = getStartOfLastWeek();
        let endOfLastWeek = new Date(startOfLastWeek);
        endOfLastWeek.setDate(endOfLastWeek.getDate() + 6); // Move to last week's Sunday
        endOfLastWeek.setHours(23, 59, 59, 999); // End of the day

        return endOfLastWeek;
    };

    const getStartOfLastMonth = () => {
        let date = new Date();
        date.setMonth(date.getMonth() - 1, 1); // Set to the 1st of the previous month
        date.setHours(0, 0, 0, 0);
        return date;
    };

    const getEndOfLastMonth = () => {
        let date = new Date();
        date.setDate(1); // Move to the 1st of the current month
        date.setHours(0, 0, 0, 0);
        date.setMilliseconds(-1); // Set to the last moment of the previous month
        return date;
    };

    const getStartOfLast3Months = () => dayjs().subtract(3, "month").startOf("month").toISOString();
    const getStartOfLast6Months = () => dayjs().subtract(6, "month").startOf("month").toISOString();
    const getStartOfLastYear = () => dayjs().subtract(1, "year").startOf("month").toISOString();

    useEffect(() => {
        let fromDateTemp;
        let toDateTemp = new Date();

        switch (filterPeriod) {
            case "today":
                fromDateTemp = new Date().setHours(0, 0, 0, 0);
                toDateTemp.setHours(23, 59, 59, 999);
                break;
            case "lastWeek":
                fromDateTemp = getStartOfLastWeek();
                toDateTemp = getEndOfLastWeek().getTime();
                break;
            case "lastMonth":
                fromDateTemp = getStartOfLastMonth();
                toDateTemp = getEndOfLastMonth().getTime();
                break;
            case "Custom":
                fromDateTemp = fromDate;
                toDateTemp = toDate;
                break;
            case "Last3Months":
                fromDateTemp = new Date(getStartOfLast3Months()).getTime();
                toDateTemp = toDateTemp.getTime();
                break;
            case "Last6Months":
                fromDateTemp = new Date(getStartOfLast6Months()).getTime();
                toDateTemp = toDateTemp.getTime();
                break;
            case "LastYear":
                fromDateTemp = new Date(getStartOfLastYear()).getTime();
                toDateTemp = toDateTemp.getTime();
                break;
            default:
                fromDateTemp = creation ? new Date(creation) : new Date();
                toDateTemp = toDateTemp.getTime();
        }

        setFromDate((prev) => {
            const prevTime = prev?.getTime?.() ?? 0;
            const newTime = new Date(fromDateTemp).getTime();
            return prevTime !== newTime ? new Date(fromDateTemp) : prev;
        });

        setToDate((prev) => {
            const prevTime = prev?.getTime?.() ?? 0;
            const newTime = new Date(toDateTemp).getTime();
            return prevTime !== newTime ? new Date(toDateTemp) : prev;
        });
    }, [filterPeriod, creation]);

    const filteredHistory = useMemo(() => {
        if (!Array.isArray(accountHistory)) return [];

        const now = new Date();
        let _from = null;
        let _to = null;

        switch (filterPeriod) {
            case "today":
                _from = new Date().setHours(0, 0, 0, 0);
                _to = new Date().setHours(23, 59, 59, 999);
                break;
            case "lasWeek":
                _from = getStartOfLastWeek().getTime();
                _to = getEndOfLastWeek().getTime();
                break;
            case "lastMonth":
                _from = getStartOfLastMonth().getTime();
                _to = getEndOfLastMonth().getTime();
                break;
            case "Last3Month":
                _from = new Date(getStartOfLast3Months()).getTime();
                _to = now.getTime();
                break;
            case "Last6Month":
                _from = new Date(getStartOfLast6Months()).getTime();
                _to = now.getTime();
                break;
            case "LastYear":
                _from = new Date(getStartOfLastYear()).getTime();
                _to = now.getTime();
                break;
            case "Custom":
                _from = fromDate ? new Date(fromDate).getTime() : null;
                _to = toDate ? new Date(toDate).getTime() : null;
                break;
            default:
                _from = creation ? new Date(creation).getTime() : null;
                _to = now.getTime();
        }

        return accountHistory.filter(row => {
            const rowDate = new Date(row.dateTime).getTime();

            const matchesDate =
                (!_from || rowDate >= _from) &&
                (!_to || rowDate <= _to);


            const matchesType =
                (selectedTabType === "Trades" && [10, 12].includes(row.type)) ||
                (selectedTabType === "Orders" && ![1, 2, 3, 4, 5, 10, 12].includes(row.type)) || // type = 6,7,8,9,11
                (selectedTabType === "Transaction" && [1, 2, 3, 4, 5].includes(row.type));

            const matchesSymbol = selectedSymbol
                ? (row.symbolData?.name || row.symbolName || "").trim().toLowerCase() === selectedSymbol.trim().toLowerCase()
                : true;

            const matchesSearch = !searchQuery ||
                Object.values(row).some(value => value?.toString().toLowerCase().includes(searchQuery.toLowerCase()));

            return matchesDate && matchesType && matchesSymbol && matchesSearch;
        });

    }, [accountHistory, filterPeriod, selectedTabType, selectedSymbol, searchQuery, creation, fromDate, toDate]);

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setFilteredHistory(filteredHistory));
    }, [filteredHistory, dispatch]);

    const handleCustomRangeClick = () => {
        setFilterPeriod('Custom');
        setCustomDateRange(true);
        setOpenModal(true);
    };
    const periods = [
        { key: 'today', value: 'Today' },
        { key: 'lastWeek', value: 'Last Week' },
        { key: 'lastMonth', value: 'Last Month' },
        { key: 'Last3Month', value: 'Last 3 Month' },
        { key: 'Last6Month', value: 'Last 6 Month' },
        { key: 'allHistory', value: 'All History' }
    ];
    return (
        <>
            <Tooltip title={t('filter')}><IoTimerOutline onClick={handleOpenMenu} style={{ cursor: 'pointer', fontSize: 20, color: darkMode ? "var(--lightText)" : "var(--darkText)" }} /></Tooltip>
            {showFilter && (
                <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleCloseMenu} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
                    <div style={{ backgroundColor: darkMode ? "#262f3f" : "", color: darkMode ? "var(--lightText)" : '#var(--darkText)' }}>

                        {periods.map((period) => (
                            <MenuItem key={period.key} onClick={() => { setFilterPeriod(period.key); }} sx={{ fontSize: 13.5 }}>{t(`${period.key}`)}</MenuItem>
                        ))}
                        <MenuItem key="Custom Range" onClick={handleCustomRangeClick} sx={{ fontSize: 13.5 }}>{t('customRange')}</MenuItem>

                        <Dialog open={openModal} onClose={() => setOpenModal(false)} >
                            <DialogContent sx={{ backgroundColor: darkMode ? "#262f3f" : "", }}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <div style={{ display: 'flex', flexDirection: "column", gap: '16px', color: darkMode ? 'var(--lightText)' : '#var(--darkText)' }}>
                                        <DatePicker label={t('fromDate')} value={dayjs(fromDate)} onChange={(newValue) => setFromDate(newValue)} renderInput={(params) => <TextField {...params} />} />
                                        <DatePicker label={t('toDate')} value={dayjs(toDate)} onChange={(newValue) => setToDate(newValue)} renderInput={(params) => <TextField {...params} />} />
                                    </div>
                                </LocalizationProvider>
                            </DialogContent>
                            <DialogActions sx={{ backgroundColor: darkMode ? "#262f3f" : "", }}>
                                <Button onClick={() => setOpenModal(false)} color="primary" variant='outlined'>{t('cancel')}</Button>
                            </DialogActions>
                        </Dialog>

                    </div>
                </Popover>
            )}
        </>
    )
}
